import axios from "axios";
import { AxiosHeaders } from "axios";
// 全局baseURL
const baseURL = "https://test.job581.cn/api";
// 导出baseURL以便其他文件使用
export { baseURL };

/**
 * Subset of AxiosRequestConfig
 * @typedef {Object} RequestConfig
 * @property {string} [url]
 * @property {"get"|"put"|"patch"|"post"|"delete"} method
 * @property {any} [params]
 * @property {any} [data]
 * @property {"arraybuffer"|"blob"|"document"|"json"|"text"|"stream"} [responseType]
 * @property {AbortSignal} [signal]
 * @property {Object} [headers]
 */

/**
 * Subset of AxiosResponse
 * @typedef {Object} ResponseConfig
 * @property {any} data
 * @property {number} status
 * @property {string} statusText
 * @property {Object} [headers]
 */

/**
 * Creates headers for HTTP requests
 * @returns {AxiosHeaders}
 */
export function headers() {
	const header = new AxiosHeaders();
	const localJwt = localStorage.getItem("job-gpt-jwt") || undefined;
	if (localJwt) {
		header.set("jwt-token", localJwt);
	}
	
	// 添加CSRF令牌（如果存在）
	const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
	if (csrfToken) {
		header.set("X-CSRF-Token", csrfToken);
	}
	
	return header;
}


export const axiosInstance = axios.create({
	baseURL,
	headers: headers(),
	withCredentials: true, // 确保发送跨域请求时携带cookie
});

// 添加响应拦截器
axiosInstance.interceptors.response.use(
	(response) => {
		// 如果响应包含JWT令牌头，我们可以保存它（备用方案）
		const jwtToken = response.headers['jwt-token'] || response.headers['Jwt-Token'];
		if (jwtToken) {
			localStorage.setItem('job-gpt-jwt', jwtToken);
			console.log('从响应头保存JWT令牌');
		}
		return response;
	},
	(error) => {
		// 处理错误
		if (error.response && error.response.status === 401) {
			// 可能需要清除本地存储的用户信息
			console.log('接收到未授权响应，用户可能需要重新登录');
			localStorage.removeItem('user-info');
		}
		return Promise.reject(error);
	}
);

/**
 * Client for making HTTP requests
 * @param {RequestConfig} config
 * @returns {Promise<ResponseConfig>}
 */
export const axiosClient = async (config) => {
	const promise = axiosInstance
		.request({ ...config })
		.catch((e) => {
			throw e;
		});

	return promise;
};

/**
 * 获取用户数据并存储到localStorage
 * @returns {Promise<boolean>} 是否成功获取并保存用户数据
 */
export const fetchUserData = async () => {
	try {
		const response = await axiosClient({
			method: 'get',
			url: '/user/data',
			timeout: 5000
		});

		console.log('获取用户数据响应:', response.data);

		// 如果API调用成功
		if (response.data && response.data.code === 0 && response.data.data) {
			// 用户数据
			const userData = response.data.data;
			
			// 构建用户信息对象
			const userInfo = {
				nickName: userData.nick_name || userData.nickname || '',
				avatar: userData.avatar || '',
				school: userData.school || userData.university || '',
				studentId: userData.student_id || '',
				remainingMockInterviewTime: userData.remaining_mock_interview_time || 0,
				needChangePassword: userData.need_change_password || false,
				lastLoginTime: new Date().toISOString(),
				// 可以添加其他需要的字段
			};
			
			// 保存用户信息到localStorage
			localStorage.setItem('user-info', JSON.stringify(userInfo));
			console.log('已保存用户数据到本地存储:', userInfo);
			
			return true;
		}
		
		return false;
	} catch (error) {
		console.error('获取用户数据失败:', error);
		return false;
	}
};

/**
 * 检查用户登录状态 - 基于localStorage中的用户信息
 * @returns {boolean} 用户是否已登录
 */
export const checkLoginStatus = () => {
	try {
		const userInfoStr = localStorage.getItem('user-info');
		if (!userInfoStr) return false;
		
		const userInfo = JSON.parse(userInfoStr);
		// 检查是否存在必要的字段
		return !!userInfo.nickName && !!userInfo.lastLoginTime;
	} catch (error) {
		console.error('检查登录状态时出错:', error);
		// 清除本地存储的用户信息
		localStorage.removeItem('user-info');
		return false; // 出错时假设用户未登录
	}
};

/**
 * 检查用户是否已登录（基于localStorage中的用户信息）
 * @returns {boolean} 用户是否已登录
 * @deprecated 请使用checkLoginStatus()替代
 */
export const isUserLoggedIn = checkLoginStatus;

export default axiosClient;
